
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { technologistOrderFormConfiguration } from '@/helpers/OrderHelper';
import { TechnologistFormField } from '@/interfaces/components/projectNew/TechnologistFormField';
import { orderAttachmentTypeOptions } from '@/enums/global/OrderAttachmentType';
import AttachmentAPI from '@/api/AttachmentAPI';
// @ts-ignore
import downloadFile from 'downloadjs';

@Component({
    name: 'TechnologistOrderFormInputs',
    components: {},
})
export default class TechnologistOrderFormInputs extends Vue {
    @Prop({ default: null }) private project!: any;
    @Prop({ default: () => [] }) private hiddenFields!: string[];
    @Prop({ required: true }) private value!: any;
    @Prop({ required: true }) private errors!: any;

    private get orderFormConfiguration() {
        return technologistOrderFormConfiguration;
    }

    private get regularFormFields(): TechnologistFormField[] {
        return this.orderFormConfiguration.formFields.filter((field) => field.type !== 'date' && field.name !== 'numOfOrders');
    }

    private get nonRowFields(): TechnologistFormField[] {
        return this.regularFormFields.filter(
            (field) => field.type !== 'attachment' && !this.hiddenFields.includes(field.name),
        );
    }

    private get attachmentField(): TechnologistFormField | undefined {
        return this.regularFormFields.find((field) => field.type === 'attachment');
    }

    private get attachmentTypeOptions() {
        return orderAttachmentTypeOptions;
    }

    private get selectedColorLabel(): string {
        if (!this.value.colorData) {
            return '';
        }
        return [this.value.colorData.colorName, this.value.colorData.powderName].filter(Boolean).join(', ');
    }

    private openColorPicker() {
        this.$emit('openColorPicker', { target: 'general' });
    }

    private clearColor() {
        this.value.colorData = null;
        this.$emit('input', { ...this.value });
    }

    private attachmentRows: { file: any; attachmentType: string | null; existingAttachmentId?: string | null }[] = [{ file: null, attachmentType: null }];

    private get hasAnyAttachmentRow(): boolean {
        return this.attachmentRows.some((row) => row.file);
    }

    private handleAttachmentRowFileChange(index: number, { fileList }: any) {
        this.attachmentRows[index].file = fileList.length > 0 ? fileList[fileList.length - 1] : null;
        // A newly-selected file replaces any prefilled/existing reference at this row.
        this.attachmentRows[index].existingAttachmentId = null;
        this.syncAttachmentRows();
    }

    private addAttachmentRow() {
        this.attachmentRows.push({ file: null, attachmentType: null });
    }

    private onAttachmentRowTrash(index: number) {
        const row = this.attachmentRows[index];
        if (index === 0 && row) {
            row.file = null;
            row.attachmentType = null;
            row.existingAttachmentId = null;
            this.syncAttachmentRows();
            return;
        }
        this.removeAttachmentRow(index);
    }

    private removeAttachmentRow(index: number) {
        this.attachmentRows.splice(index, 1);
        if (this.attachmentRows.length === 0) {
            this.attachmentRows.push({ file: null, attachmentType: null });
        }
        this.syncAttachmentRows();
    }

    private syncAttachmentRows() {
        if (!this.attachmentField) {
            return;
        }
        this.value[this.attachmentField.name] = this.attachmentRows.filter((row) => row.file).map((row) => row.file);
        this.value.attachmentRows = this.attachmentRows;
        this.onFormChange(this.attachmentField);
    }

    public setPrefilledAttachmentRows(rows: { file: any; attachmentType: string | null; existingAttachmentId?: string | null }[]) {
        this.attachmentRows = rows.length > 0 ? rows : [{ file: null, attachmentType: null }];
        this.syncAttachmentRows();
    }

    private customRequest({ file, onSuccess, onError }: any) {
        setTimeout(() => {
            onSuccess(file);
        }, 1000);
    }

    private async handlePreviewFile(file: any) {
        if (!file.url) {
            return;
        }
        let blob;
        try {
            blob = await AttachmentAPI.getBlob(file.url);
        } catch (e) {
            return;
        }
        // @ts-ignore
        downloadFile(blob.data, file.name);
    }

    private onFormChange(field: TechnologistFormField) {
        this.$emit('input', { ...this.value });
        if (!this.errors[field.name]) {
            return;
        }
        this.$emit('validateField', field.name);
    }

    @Watch('value.attachmentRows', { immediate: true })
    private onValueAttachmentRowsChanged(rows: any[] | undefined) {
        if (rows && rows.length > 0) {
            this.attachmentRows = rows;
        }
    }

    private mounted() {
        if (!this.project) {
            return;
        }
        this.value.client = this.project?.client?.name ?? this.project?.clientOrB2B?.name ?? '';
        this.value.commercialist = this.project?.assignedUser?.name ?? '';
        this.value.projectConnection = this.project?.connection ?? '';
    }
}
